import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  VARIATION_NAME_MAX,
  normalizeVariationName,
  validateVariationName,
} from "@/lib/variationName";

interface CreateVariationModalProps {
  isOpen: boolean;
  onClose: () => void;
  emailPrefix: string;
  existingNames: string[];
  existingTags: string[];
  onCreate: (name: string) => void;
  isPending?: boolean;
}

export function CreateVariationModal({
  isOpen,
  onClose,
  emailPrefix,
  existingNames,
  existingTags,
  onCreate,
  isPending = false,
}: CreateVariationModalProps) {
  const [nameInput, setNameInput] = useState("");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      setNameInput("");
      setError(null);
    }
  }, [isOpen]);

  const normalized = normalizeVariationName(nameInput);

  const validate = (): string | null => {
    const fieldError = validateVariationName(nameInput);
    if (fieldError) return fieldError;
    if (
      existingNames.some(
        (n) => n.toLowerCase() === normalized.toLowerCase()
      )
    ) {
      return `A variation named "${normalized}" already exists`;
    }
    return null;
  };

  const handleSubmit = () => {
    const err = validate();
    if (err) {
      setError(err);
      return;
    }
    setError(null);
    onCreate(normalized);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !isPending) {
      handleSubmit();
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="font-display">Create Variation</DialogTitle>
          <DialogDescription>
            Test alternate copy, audiences, or designs alongside your default email.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="space-y-2">
            <Label htmlFor="variation-name">Variation name</Label>
            <Input
              id="variation-name"
              value={nameInput}
              onChange={(e) => {
                setNameInput(e.target.value);
                setError(null);
              }}
              onKeyDown={handleKeyDown}
              placeholder="e.g. US Promo"
              maxLength={VARIATION_NAME_MAX}
              autoFocus
            />
            <p className="text-xs text-muted-foreground">
              Up to {VARIATION_NAME_MAX} characters. Letters, numbers, spaces, and{" "}
              <code className="font-mono">- _ . ( )</code> allowed.
            </p>
            {error && <p className="text-sm text-destructive">{error}</p>}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isPending}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!normalized || isPending}
            className="bg-foreground text-primary-foreground hover:bg-acid-green hover:text-foreground"
          >
            {isPending ? "Creating..." : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
